import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  FormLayout,
  TextField,
  Button,
  Banner,
  BlockStack,
  InlineStack,
  Text,
  Divider,
  Badge,
  Tabs,
  Layout
} from '@shopify/polaris';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import axios from 'axios';

interface UserProfile {
  id: number;
  email: string;
  role: string;
}

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [profileForm, setProfileForm] = useState({
    email: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  });

  useEffect(() => {
    if (user?.role !== 'support_manager') {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_CONFIG.FULL_BASE_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const userData = response.data.data.user;
        setProfile(userData);
        setProfileForm({ email: userData.email });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_CONFIG.FULL_BASE_URL}/profile`, {
        user: profileForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('Profile updated successfully');
        setProfile(response.data.data.user);
        // Update the auth context with new email
        updateUser({ email: response.data.data.user.email });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API_CONFIG.FULL_BASE_URL}/profile/change_password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
        new_password_confirmation: passwordForm.new_password_confirmation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('Password updated successfully');
        setPasswordForm({
          current_password: '',
          new_password: '',
          new_password_confirmation: ''
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const handleProfileInputChange = (field: string, value: string) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordInputChange = (field: string, value: string) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
  };

  const tabs = [
    {
      id: 'profile',
      content: 'Profile Information',
      accessibilityLabel: 'Profile Information',
      panelID: 'profile-panel',
    },
    {
      id: 'password',
      content: 'Change Password',
      accessibilityLabel: 'Change Password',
      panelID: 'password-panel',
    },
  ];

  if (loading) {
    return (
      <Page title="Profile" fullWidth>
        <Card>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <Text variant="bodyMd" as="p">Loading profile...</Text>
          </div>
        </Card>
      </Page>
    );
  }

  // Support Manager Profile View
  if (user?.role === 'support_manager') {
    return (
      <Page title="Profile" fullWidth>
        <Layout>
          <Layout.Section>
            <Banner tone="info">
              <p>
                As a support manager, you can view your profile information but cannot modify it through this interface.
                For profile changes, please contact your system administrator.
              </p>
            </Banner>
          </Layout.Section>
          
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">
                  Support Manager Profile
                </Text>
                <BlockStack gap="200">
                  <Text variant="bodyMd" as="p">
                    <strong>Email:</strong> {user.email}
                  </Text>
                  <Text variant="bodyMd" as="p">
                    <strong>Role:</strong> Support Manager
                  </Text>
                  <Text variant="bodyMd" as="p">
                    <strong>Access Level:</strong> Client Management & Billing
                  </Text>
                </BlockStack>
                <Divider />
                <Text variant="bodyMd" as="p" tone="subdued">
                  For profile modifications or password changes, please contact your system administrator.
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title="Profile" fullWidth>
      <BlockStack gap="500">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}

        {success && (
          <Banner tone="success" onDismiss={() => setSuccess(null)}>
            {success}
          </Banner>
        )}

        <Card>
          <Tabs
            tabs={tabs}
            selected={selectedTab}
            onSelect={setSelectedTab}
          >
            <div style={{ padding: '20px' }}>
              {selectedTab === 0 && (
                <BlockStack gap="400">
                  <div>
                    <Text variant="headingMd" as="h2">Profile Information</Text>
                    <Text variant="bodyMd" as="p" tone="subdued">
                      Update your personal information and email address.
                    </Text>
                  </div>

                  <Divider />

                  <FormLayout>
                    <TextField
                      label="Email Address"
                      type="email"
                      value={profileForm.email}
                      onChange={(value) => handleProfileInputChange('email', value)}
                      autoComplete="email"
                      helpText="This email will be used for login and notifications"
                    />

                    <div style={{ padding: '12px 16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                      <InlineStack align="space-between">
                        <div>
                          <Text variant="bodyMd" as="p" fontWeight="semibold">User ID</Text>
                          <Text variant="bodySm" as="p" tone="subdued">
                            Internal identifier for your account
                          </Text>
                        </div>
                        <Badge tone="info">{profile?.id?.toString()}</Badge>
                      </InlineStack>
                    </div>

                    <div style={{ padding: '12px 16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                      <InlineStack align="space-between">
                        <div>
                          <Text variant="bodyMd" as="p" fontWeight="semibold">Role</Text>
                          <Text variant="bodySm" as="p" tone="subdued">
                            Your current role in the system
                          </Text>
                        </div>
                        <Badge tone="info">{profile?.role}</Badge>
                      </InlineStack>
                    </div>
                  </FormLayout>

                  <InlineStack gap="300" align="end">
                    <Button
                      variant="primary"
                      onClick={handleProfileSubmit}
                      loading={saving}
                      disabled={!profileForm.email.trim()}
                    >
                      Save Changes
                    </Button>
                  </InlineStack>
                </BlockStack>
              )}

              {selectedTab === 1 && (
                <BlockStack gap="400">
                  <div>
                    <Text variant="headingMd" as="h2">Change Password</Text>
                    <Text variant="bodyMd" as="p" tone="subdued">
                      Update your password to keep your account secure.
                    </Text>
                  </div>

                  <Divider />

                  <FormLayout>
                    <TextField
                      label="Current Password"
                      type="password"
                      value={passwordForm.current_password}
                      onChange={(value) => handlePasswordInputChange('current_password', value)}
                      autoComplete="current-password"
                      helpText="Enter your current password to verify your identity"
                    />

                    <TextField
                      label="New Password"
                      type="password"
                      value={passwordForm.new_password}
                      onChange={(value) => handlePasswordInputChange('new_password', value)}
                      autoComplete="new-password"
                      helpText="Choose a strong password with at least 8 characters"
                    />

                    <TextField
                      label="Confirm New Password"
                      type="password"
                      value={passwordForm.new_password_confirmation}
                      onChange={(value) => handlePasswordInputChange('new_password_confirmation', value)}
                      autoComplete="new-password"
                      helpText="Re-enter your new password to confirm"
                    />
                  </FormLayout>

                  <InlineStack gap="300" align="end">
                    <Button
                      variant="primary"
                      onClick={handlePasswordSubmit}
                      loading={saving}
                      disabled={
                        !passwordForm.current_password.trim() ||
                        !passwordForm.new_password.trim() ||
                        !passwordForm.new_password_confirmation.trim() ||
                        passwordForm.new_password !== passwordForm.new_password_confirmation
                      }
                    >
                      Update Password
                    </Button>
                  </InlineStack>
                </BlockStack>
              )}
            </div>
          </Tabs>
        </Card>
      </BlockStack>
    </Page>
  );
};

export default Profile;
